import React, { Component, useState } from 'react';
import { StyleSheet, View, FlatList, ScrollView } from 'react-native';
import { observer } from 'mobx-react';

import Button from '../shared/Button';
import Picker from '../shared/Picker';
import Alert from '../shared/Alert';
import Modal from '../shared/Modal';
import { formatNumber } from '../../utils/utils';
import { sendAction } from '../../lib/apiCalls';

import appStore from '../../stores/app';
import heroStore, {
  removeThing,
  dressUndressThing,
  undressThings,
  applyThing,
} from '../../stores/hero';
import { FULL_WIDTH as BODY_WIDTH } from '../common/Body';
import ThingItem from '../common/ThingItem';
import WarriorLoginForm from '../common/WarriorLoginForm';

import { thingCanBeDressed, thingCanBeApplied, isThingSendable } from '../../utils/warriorUtils';
import { t, action, getLocalValue } from '../../utils/appUtils';
import { getThing, thingHasAction } from '../../utils/thingUtils';

import { IS_WEB } from '../../lib/appConstants';

import { THING_TYPES } from '../../lib/constants';
import { pOrT, PADDING, PADDING1, WINDOW_WIDTH, BUTTON_HEIGHT, CONTENT_HEIGHT } from '../styleConstants';

import { ThingType, TThingType, WarriorThingType, WarriorType } from '../../lib/types';

import logImport from '../../lib/log';
const log = logImport as (name: string, params?: Record<string, any>) => void;
import { MIN_LVL_TO_SEND } from '../../lib/config';

const WIDTH = WINDOW_WIDTH - BODY_WIDTH - 3 * PADDING;

export function filterThings(
  things: ThingType[],
  id: string,
  filter: string | null | undefined,
  additionalFn?: (thing: ThingType) => any,
): boolean {
  const thing = getThing(things, id);
  const additionalFnResult = additionalFn ? additionalFn(thing) : true;

  if (filter === null || filter === 'all') return true && additionalFnResult;

  return thing.type === filter && additionalFnResult;
}

export const styles = StyleSheet.create({
  container: {
    marginTop: PADDING,
    height: CONTENT_HEIGHT - BUTTON_HEIGHT - PADDING - PADDING1,
    ...(IS_WEB
      ? {
          height: 'calc(100vh - 450px)' as any,
        }
      : null),
  },
});

// eslint-disable-next-line max-len
export const applyAction = (thing: ThingType, heroThing: WarriorThingType, onOk?: () => void) =>
  action(() => {
    Alert.alert(t('things.applyThing', { name: getLocalValue(thing) }), t('areYouSure'), [
      { text: t('cancel'), style: 'cancel' },
      {
        text: t('yes'),
        onPress: async () => {
          log('applyThingAction', {
            warriorLogin: heroStore.hero.login,
            warriorThingId: heroThing.id,
          });
          await applyThing(heroThing.id);
          if (onOk) onOk();
        },
      },
    ]);
  });

type FilterPickerProps = {
  showAll?: boolean;
  filter?: (key: TThingType | 'all') => boolean;
  [key: string]: any;
};

export const FilterPicker = ({
  showAll = true,
  filter = () => true,
  ...props
}: FilterPickerProps) => {
  let data: { key: string; label: string }[] = [
    { key: THING_TYPES.SWORD, label: t('things.types.sword') },
    { key: THING_TYPES.AXE, label: t('things.types.axe') },
    { key: THING_TYPES.KNIFE, label: t('things.types.knife') },
    { key: THING_TYPES.CLUBS, label: t('things.types.clubs') },
    { key: THING_TYPES.SHIELD, label: t('things.types.shield') },
    { key: THING_TYPES.HELMET, label: t('things.types.helmet') },
    { key: THING_TYPES.ARMOR, label: t('things.types.armor') },
    { key: THING_TYPES.CAPE, label: t('things.types.cape') },
    { key: THING_TYPES.BELT, label: t('things.types.belt') },
    { key: THING_TYPES.PANTS, label: t('things.types.pants') },
    { key: THING_TYPES.BRACER, label: t('things.types.bracer') },
    { key: THING_TYPES.GLOVES, label: t('things.types.gloves') },
    { key: THING_TYPES.BOOTS, label: t('things.types.boots') },
    { key: THING_TYPES.RING, label: t('things.types.ring') },
    { key: THING_TYPES.AMULET, label: t('things.types.amulet') },
    { key: THING_TYPES.ELIXIR, label: t('things.types.elixir') },
    { key: THING_TYPES.SCROLL, label: t('things.types.scroll') },
  ];

  data = data.filter((item) => filter(item.key as TThingType | 'all'));

  if (showAll) {
    data.unshift({ key: 'all', label: t('all') });
  }

  return (
    <Picker
      data={data.map((item) => ({ ...item, label: item.label.toUpperCase() }))}
      placeholder={t('filter').toUpperCase()}
      {...(props as any)}
    />
  );
};

type Props = {};
type State = {
  filter: string | null;
  showSendMoneyForm: boolean;
  showSendThingForm: boolean;
  warrior: WarriorType | null;
  fields: {
    login: string;
    money: string;
  };
};

const SendMoneyForm = ({ onHide }: { onHide: () => void }) => {
  const [fields, setFields] = useState({
    login: '',
    money: '',
  });
  const [selectedWarrior, setSelectedWarrior] = useState<any>(null);

  const validate = () => {
    const money = formatNumber(Number(fields.money));
    if (!fields.login || !money || money < 0) return t('errors.allFieldsRequired');

    if (!selectedWarrior) return t('warriorNotFound');
    if (money > heroStore.hero.money) return t('errors.notEnoughMoney');
    if (selectedWarrior.login === heroStore.hero.login) return t('errors.cantSendToSelf');
    if (selectedWarrior.level < MIN_LVL_TO_SEND) return t('errors.cantSendToBeginner');
  };

  const onOk = () => {
    const validateMsg = validate();

    if (validateMsg) {
      appStore.showMessage({ type: 'error', text: validateMsg });
      return;
    }

    action(async () => {
      log('sendMoneyAction', {
        warriorLogin: heroStore.hero.login,
        warriorId: selectedWarrior.id,
        amount: formatNumber(Number(fields.money)),
      });

      await sendAction('sendMoney', {
        warriorId: selectedWarrior.id,
        amount: formatNumber(Number(fields.money)),
      });

      onHide();

      appStore.showMessage({
        type: 'success',
        text: t('things.moneySent'),
      });
    });
  };

  return (
    <Modal
      size={{
        width: pOrT(500, 550),
        height: pOrT(295, 220),
      }}
      title={t('things.sendMoney')}
      onHide={() => {
        setFields({
          login: '',
          money: '',
        });
        setSelectedWarrior(null);
        onHide();
      }}
      marginTop={IS_WEB ? 150 : undefined}
      onOk={() => {
        onOk();
      }}
      warriorListener
      okLabel={t('send').toUpperCase()}
    >
      <View
        style={{
          marginTop: 40,
          alignItems: 'center',
        }}
      >
        <ScrollView
          style={{
            height: IS_WEB ? 440 : pOrT(210, 390),
            width: 480,
          }}
          contentContainerStyle={{
            alignItems: 'center',
            paddingBottom: IS_WEB ? 0 : pOrT(150, 200),
          }}
        >
          {[
            {
              name: 'login',
              label: t('login'),
              type: 'TEXT',
            },
            {
              name: 'money',
              label: t('money'),
              type: 'NUMBER',
            },
          ].map((item) => (
            <WarriorLoginForm
              item={item}
              key={item.name}
              fields={fields}
              setFields={setFields}
              selectedWarrior={selectedWarrior}
              setSelectedWarrior={setSelectedWarrior}
            />
          ))}
        </ScrollView>
      </View>
    </Modal>
  );
};

const SendThingForm = ({ onHide, warriorThing }: { onHide: () => void; warriorThing: any }) => {
  const [fields, setFields] = useState({
    login: '',
  });
  const [selectedWarrior, setSelectedWarrior] = useState<any>(null);

  const validate = () => {
    if (!fields.login) return t('errors.allFieldsRequired');
    if (!selectedWarrior) return t('warriorNotFound');
    if (selectedWarrior.login === heroStore.hero.login) return t('errors.cantSendToSelf');
    if (selectedWarrior.level < MIN_LVL_TO_SEND) return t('errors.cantSendToBeginner');
  };

  const onOk = () => {
    const validateMsg = validate();

    if (validateMsg) {
      appStore.showMessage({ type: 'error', text: validateMsg });
      return;
    }

    action(async () => {
      log('sendThingAction', {
        warriorLogin: heroStore.hero.login,
        warriorId: selectedWarrior.id,
        warriorThing: warriorThing.id,
      });

      await sendAction('sendThing', {
        warriorId: selectedWarrior.id,
        warriorThingId: warriorThing.id,
      });

      onHide();

      appStore.showMessage({
        type: 'success',
        text: t('things.thingSent'),
      });
    });
  };

  return (
    <Modal
      size={{
        width: pOrT(500, 600),
        height: pOrT(320, 360),
      }}
      title={t('things.sendThing')}
      onHide={() => {
        setFields({
          login: '',
        });
        setSelectedWarrior(null);
        onHide();
      }}
      marginTop={IS_WEB ? 150 : undefined}
      onOk={() => {
        onOk();
      }}
      warriorListener
      okLabel={t('send').toUpperCase()}
    >
      <View
        style={{
          marginTop: 40,
          alignItems: 'center',
        }}
      >
        {[
          {
            name: 'login',
            label: t('login'),
            type: 'TEXT',
          },
        ].map((item) => (
          <WarriorLoginForm
            item={item}
            key={item.name}
            fields={fields}
            setFields={setFields}
            selectedWarrior={selectedWarrior}
            setSelectedWarrior={setSelectedWarrior}
          />
        ))}

        <ThingItem
          warrior={heroStore.hero}
          width={pOrT(467, 550)}
          warriorThingOrId={warriorThing}
        />
      </View>
    </Modal>
  );
};

@observer
export default class Inventory extends Component<Props, State> {
  state: State = {
    filter: null,
    showSendMoneyForm: false,
    showSendThingForm: false,
    warrior: null,
    fields: {
      login: '',
      money: '',
    },
  };

  sendThing: any = null;

  renderActions() {
    const { filter } = this.state;

    return (
      <View style={{ flexDirection: 'row' }}>
        <Button
          disabled={!heroStore.dressedThings.length}
          onPress={() => {
            if (heroStore.dressedThings.length > 5) {
              Alert.alert(t('things.undress'), t('areYouSure'), [
                { text: t('cancel'), style: 'cancel' },
                { text: t('yes'), onPress: () => undressThings() },
              ]);
            } else {
              undressThings();
            }
          }}
        >
          {t('things.undress').toUpperCase()}
        </Button>
        <FilterPicker
          style={{ marginLeft: PADDING }}
          value={filter}
          onChange={(value: string) => {
            this.setState({ filter: value });
          }}
        />
        {heroStore.hero.level >= MIN_LVL_TO_SEND ? (
          <Button
            sizeObj={{
              width: 135,
              height: BUTTON_HEIGHT,
            }}
            style={{ marginLeft: PADDING }}
            onPress={() => {
              this.setState({ showSendMoneyForm: true });
            }}
          >
            {t('things.sendMoney').toUpperCase()}
          </Button>
        ) : null}
        {this.state.showSendMoneyForm ? (
          <SendMoneyForm
            onHide={() => {
              this.setState({ showSendMoneyForm: false });
            }}
          />
        ) : null}
      </View>
    );
  }

  render() {
    const { filter, showSendThingForm } = this.state;
    return (
      <View
        style={{
          width: WIDTH,
        }}
      >
        {this.renderActions()}
        <FlatList
          style={styles.container}
          data={heroStore.undressedThings.filter((item: any) =>
            filterThings(appStore.initData.things, item.thing, filter),
          )}
          renderItem={({ item: heroThing, index }: { item: any; index: number }) => (
            <ThingItem
              width={WIDTH}
              key={heroThing.id}
              warrior={heroStore.hero}
              warriorThingOrId={heroThing}
              index={index}
              actionsRenderer={(thing: ThingType) => (
                <View style={{ marginTop: -PADDING }}>
                  {thingCanBeDressed(heroStore.hero, thing) ? (
                    <Button
                      style={{ marginTop: PADDING }}
                      onPress={() =>
                        action(() => {
                          log('dressThingAction', {
                            warriorLogin: heroStore.hero.login,
                            warriorThingId: heroThing.id,
                          });
                          dressUndressThing(true, heroThing.id);
                        })
                      }
                      hitSlop={{
                        top: 0,
                        bottom: 0,
                      }}
                    >
                      {t('things.dress').toUpperCase()}
                    </Button>
                  ) : null}
                  {thingCanBeApplied(heroStore.hero, thing) && thingHasAction(thing) ? (
                    <Button
                      style={{ marginTop: PADDING }}
                      onPress={() => applyAction(thing, heroThing)}
                      hitSlop={{
                        top: 0,
                        bottom: 0,
                      }}
                    >
                      {t('apply').toUpperCase()}
                    </Button>
                  ) : null}
                  <Button
                    onPress={() =>
                      action(() => {
                        Alert.alert(
                          t('things.removeThing', {
                            name: getLocalValue(thing),
                          }),
                          t('areYouSure'),
                          [
                            { text: t('cancel'), style: 'cancel' },
                            {
                              text: t('yes'),
                              onPress: () => {
                                log('removeThingAction', {
                                  warriorLogin: heroStore.hero.login,
                                  warriorThingId: heroThing.id,
                                });
                                removeThing(heroThing.id);
                              },
                            },
                          ],
                        );
                      })
                    }
                    style={{ marginTop: PADDING }}
                    hitSlop={{
                      top: 0,
                      bottom: 0,
                    }}
                    variant="red"
                  >
                    {t('things.remove').toUpperCase()}
                  </Button>
                  {isThingSendable(heroThing) && heroStore.hero.level >= MIN_LVL_TO_SEND ? (
                    <Button
                      style={{ marginTop: PADDING }}
                      onPress={() => {
                        this.sendThing = heroThing;
                        this.setState({ showSendThingForm: true });
                      }}
                      hitSlop={{
                        top: 0,
                        bottom: 0,
                      }}
                    >
                      {t('send').toUpperCase()}
                    </Button>
                  ) : null}
                </View>
              )}
            />
          )}
        />
        {showSendThingForm ? (
          <SendThingForm
            warriorThing={this.sendThing}
            onHide={() => {
              this.sendThing = null;
              this.setState({ showSendThingForm: false });
            }}
          />
        ) : null}
      </View>
    );
  }
}
